const clampProtectionProgress = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

const protectionRange = (value, start, end) =>
  clampProtectionProgress((value - start) / Math.max(end - start, 0.0001));

const protectionEase = (value) => value * value * (3 - 2 * value);

class TootoProductProtectionReveal extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.scene = this.querySelector('[data-tooto-protection-scene]');
    this.stage = this.querySelector('[data-tooto-protection-stage]');
    this.header = this.querySelector('[data-tooto-protection-header]');
    this.artwork = this.querySelector('.tooto-product-protection-reveal__artwork');
    this.protectedArtwork = this.querySelector('.tooto-product-protection-reveal__protected');
    this.hideSiteHeader = this.dataset.hideSiteHeader === 'true';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.scene || !this.stage || !this.artwork) return;

    if (this.dataset.scrollEnabled !== 'true' || this.reducedMotion) {
      this.showStaticState();
      return;
    }

    this.currentProgress = 0;
    this.targetProgress = 0;
    this.frameRequest = 0;
    this.metricsDirty = true;
    this.renderImmediately = true;

    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.render = this.render.bind(this);
    this.scrollContainer = this.closest('.page-wrapper') || document.querySelector('.page-wrapper');

    window.addEventListener('scroll', this.handleViewportChange, { passive: true });
    window.addEventListener('resize', this.handleViewportChange, { passive: true });
    this.scrollContainer?.addEventListener('scroll', this.handleViewportChange, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.metricsDirty = true;
        this.requestRender(true);
      });
      this.resizeObserver.observe(this.scene);
      this.resizeObserver.observe(this.stage);
    }

    this.classList.add('is-ready');
    this.refreshMetrics();
    this.targetProgress = this.getScrollProgress();
    this.currentProgress = this.targetProgress;
    this.applyProgress(this.currentProgress);
    this.updateSiteHeaderVisibility();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleViewportChange);
    window.removeEventListener('resize', this.handleViewportChange);
    this.scrollContainer?.removeEventListener('scroll', this.handleViewportChange);
    this.resizeObserver?.disconnect();
    if (this.frameRequest) cancelAnimationFrame(this.frameRequest);
    this.setSiteHeaderHidden(false);
  }

  showStaticState() {
    this.classList.add('is-static');
    this.refreshMetrics();
    this.applyProgress(1);
  }

  handleViewportChange(event) {
    if (event?.type === 'resize') this.metricsDirty = true;
    this.requestRender(event?.type === 'resize');
  }

  requestRender(immediate = false) {
    if (immediate) this.renderImmediately = true;
    if (this.frameRequest) return;
    this.frameRequest = requestAnimationFrame(this.render);
  }

  refreshMetrics() {
    this.metricsDirty = false;
    this.stickyTop = Number.parseFloat(window.getComputedStyle(this.stage).top) || 0;
    this.scrollableDistance = Math.max(1, this.scene.offsetHeight - this.stage.offsetHeight);
    const finalFrontScale = Number(this.dataset.finalFrontScale);
    this.finalFrontScale = clampProtectionProgress(Number.isFinite(finalFrontScale) ? finalFrontScale / 100 : 0.8, 0.5, 1);

    const minimumArtworkGap = window.innerWidth < 768 ? 28 : window.innerWidth <= 1024 ? 40 : 56;
    const headerBottom = this.header ? this.header.offsetTop + this.header.offsetHeight : 0;
    const artworkTop = this.artwork.offsetTop;
    const artworkBottomSpace = Math.max(
      0,
      this.stage.clientHeight - artworkTop - this.artwork.offsetHeight
    );
    const protectedOverflow = this.protectedArtwork
      ? Math.max(
          0,
          this.protectedArtwork.offsetTop + this.protectedArtwork.offsetHeight - this.artwork.offsetHeight
        )
      : 0;
    const requiredArtworkShift = Math.max(0, headerBottom + minimumArtworkGap - artworkTop);
    const safeArtworkShift = Math.max(0, artworkBottomSpace - protectedOverflow - 24);
    const artworkFlowOffset = Math.min(requiredArtworkShift, safeArtworkShift);

    this.style.setProperty('--tooto-protection-artwork-flow-offset', `${artworkFlowOffset.toFixed(2)}px`);
    this.artworkHeight = Math.max(1, this.artwork.offsetHeight);
    this.protectedHeight = Math.max(1, this.protectedArtwork?.offsetHeight || this.artworkHeight * 1.16);
    if (this.protectedArtwork) {
      const artworkRect = this.artwork.getBoundingClientRect();
      const protectedRect = this.protectedArtwork.getBoundingClientRect();
      this.protectedTop = protectedRect.top - artworkRect.top;
    } else {
      this.protectedTop = 0;
    }
  }

  getScrollProgress() {
    const sceneTop = this.scene.getBoundingClientRect().top;
    return clampProtectionProgress((this.stickyTop - sceneTop) / this.scrollableDistance);
  }

  setSiteHeaderHidden(hidden) {
    if (hidden && document.body.classList.contains('tooto-menu-drawer-open')) {
      hidden = false;
    }

    if (!this.hideSiteHeader || this.siteHeaderHidden === hidden) return;
    this.siteHeaderHidden = hidden;
    document.body.classList.toggle('tooto-protection-header-hidden', hidden);
  }

  updateSiteHeaderVisibility() {
    const sceneRect = this.scene.getBoundingClientRect();
    const menuDrawerOpen =
      document.body.classList.contains('tooto-menu-drawer-open') ||
      document.querySelector('#header-group .menu-drawer-container[open]');
    const isPinned =
      !menuDrawerOpen &&
      sceneRect.top <= this.stickyTop + 1 &&
      sceneRect.bottom >= this.stickyTop + this.stage.offsetHeight + 1;
    this.setSiteHeaderHidden(isPinned);
  }

  render() {
    this.frameRequest = 0;
    if (!this.isConnected) return;
    if (this.metricsDirty) this.refreshMetrics();

    this.targetProgress = this.getScrollProgress();
    this.updateSiteHeaderVisibility();

    if (this.renderImmediately) {
      this.currentProgress = this.targetProgress;
      this.renderImmediately = false;
    } else {
      this.currentProgress += (this.targetProgress - this.currentProgress) * 0.18;
    }

    if (Math.abs(this.targetProgress - this.currentProgress) < 0.0005) {
      this.currentProgress = this.targetProgress;
    }

    this.applyProgress(this.currentProgress);

    if (Math.abs(this.targetProgress - this.currentProgress) >= 0.0005) {
      this.requestRender();
    }
  }

  applyProgress(progress) {
    const cardProgress = protectionEase(protectionRange(progress, 0.04, 0.32));
    const scanProgress = protectionEase(protectionRange(progress, 0.34, 0.94));
    const lightRevealProgress = protectionEase(protectionRange(progress, 0.32, 0.42));
    const labelProgress = protectionEase(protectionRange(progress, 0.4, 0.5));
    const lightExitProgress = 1 - protectionEase(protectionRange(progress, 0.94, 1));

    const frontScale = 1 + (this.finalFrontScale - 1) * cardProgress;
    const frontTop = this.artworkHeight * (1 - this.finalFrontScale);
    const frontHeight = this.artworkHeight * this.finalFrontScale;
    const protectedBottom = this.protectedTop + this.protectedHeight;
    const scanStart = Math.min(frontTop, this.protectedTop);
    const scanEnd = Math.max(frontTop + frontHeight, protectedBottom);
    const lightCenter = scanStart + (scanEnd - scanStart) * scanProgress;
    const protectedReveal = clampProtectionProgress(
      (lightCenter - this.protectedTop) / this.protectedHeight
    );
    const protectedClipBottom = 100 * (1 - protectedReveal);
    const lightVisibility = lightRevealProgress * lightExitProgress;

    this.style.setProperty('--tooto-protection-front-scale', frontScale.toFixed(5));
    this.style.setProperty('--tooto-protection-sleeve-opacity', lightRevealProgress.toFixed(4));
    this.style.setProperty('--tooto-protection-sleeve-clip-bottom', `${protectedClipBottom.toFixed(3)}%`);
    this.style.setProperty('--tooto-protection-light-top', `${lightCenter.toFixed(2)}px`);
    this.style.setProperty('--tooto-protection-light-opacity', lightVisibility.toFixed(4));
    this.style.setProperty('--tooto-protection-light-scale', (0.08 + 0.92 * lightRevealProgress).toFixed(5));
    this.style.setProperty('--tooto-protection-label-opacity', (labelProgress * lightExitProgress).toFixed(4));
  }
}

if (!customElements.get('tooto-product-protection-reveal')) {
  customElements.define('tooto-product-protection-reveal', TootoProductProtectionReveal);
}
